import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  TextField, Select, MenuItem, FormControl, InputLabel,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, InputAdornment, Divider,
  Tabs, Tab, LinearProgress, Tooltip, ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Search, Close, Rocket, Public, Star,
  FilterList, Clear, Timeline, Language,
  EmojiEvents, Science, GridView, ViewList,
} from '@mui/icons-material';

// ─── Complete Space Missions Database ────────────────────────────────────────
const MISSIONS = [
  // ── USA / NASA ──────────────────────────────────────────────────────────────
  {
    id: 1, name: 'Apollo 11', country: 'USA', agency: 'NASA',
    launchYear: 1969, endYear: 1969, status: 'Completed',
    type: 'Crewed Lunar', target: 'Moon',
    description: 'First crewed mission to land on the Moon. Neil Armstrong and Buzz Aldrin became the first humans to walk on the lunar surface on July 20, 1969.',
    achievements: ['First Moon landing', 'First humans on another world', 'Returned 21.5 kg of lunar samples'],
    crew: ['Neil Armstrong', 'Buzz Aldrin', 'Michael Collins'],
    color: '#ffffff', flag: '🇺🇸', icon: '🌕',
    significance: 'Historic',
  },
  {
    id: 2, name: 'Voyager 1', country: 'USA', agency: 'NASA',
    launchYear: 1977, endYear: null, status: 'Active',
    type: 'Deep Space Probe', target: 'Interstellar Space',
    description: 'The farthest human-made object from Earth. Crossed into interstellar space in 2012 and continues to transmit data from beyond our solar system.',
    achievements: ['Farthest human-made object', 'First spacecraft in interstellar space', 'Detailed images of Jupiter and Saturn'],
    color: '#ffa726', flag: '🇺🇸', icon: '🚀',
    significance: 'Historic',
  },
  {
    id: 3, name: 'Hubble Space Telescope', country: 'USA', agency: 'NASA/ESA',
    launchYear: 1990, endYear: null, status: 'Active',
    type: 'Space Telescope', target: 'Universe',
    description: 'The most productive scientific instrument ever built. Has made over 1.5 million observations and helped determine the age and expansion rate of the universe.',
    achievements: ['Determined universe age (~13.8 billion years)', 'Discovered dark energy', 'Over 18,000 scientific papers'],
    color: '#7e57c2', flag: '🇺🇸', icon: '🔭',
    significance: 'Historic',
  },
  {
    id: 4, name: 'Mars Curiosity Rover', country: 'USA', agency: 'NASA',
    launchYear: 2011, endYear: null, status: 'Active',
    type: 'Mars Rover', target: 'Mars',
    description: 'Car-sized rover exploring Gale Crater on Mars. Has traveled over 30 km and confirmed Mars was once capable of supporting microbial life.',
    achievements: ['Confirmed ancient habitability of Mars', 'Detected organic molecules', 'Measured radiation levels for future astronauts'],
    color: '#ef5350', flag: '🇺🇸', icon: '🤖',
    significance: 'Major',
  },
  {
    id: 5, name: 'James Webb Space Telescope', country: 'USA', agency: 'NASA/ESA/CSA',
    launchYear: 2021, endYear: null, status: 'Active',
    type: 'Space Telescope', target: 'Deep Universe',
    description: 'The most powerful space telescope ever built. Observes infrared light to see the first galaxies formed after the Big Bang and study exoplanet atmospheres.',
    achievements: ['Deepest infrared images of universe', 'Detected CO2 in exoplanet atmosphere', 'Observed galaxies from 13.4 billion years ago'],
    color: '#ffffff', flag: '🇺🇸', icon: '🔭',
    significance: 'Historic',
  },
  {
    id: 6, name: 'International Space Station', country: 'International', agency: 'NASA/Roscosmos/ESA/JAXA/CSA',
    launchYear: 1998, endYear: null, status: 'Active',
    type: 'Space Station', target: 'Low Earth Orbit',
    description: 'The largest human-made structure in space, hosting continuous human presence since November 2000. A symbol of international cooperation.',
    achievements: ['25+ years of continuous human presence', 'Over 240 astronauts from 19 countries', '3,000+ experiments conducted'],
    color: '#66bb6a', flag: '🌍', icon: '🛸',
    significance: 'Historic',
  },
  {
    id: 7, name: 'Artemis I', country: 'USA', agency: 'NASA',
    launchYear: 2022, endYear: 2022, status: 'Completed',
    type: 'Crewed Lunar', target: 'Moon',
    description: 'First integrated test of NASA\'s Orion spacecraft and Space Launch System. Uncrewed mission that orbited the Moon and splashed down successfully.',
    achievements: ['Tested Orion spacecraft', 'Farthest point from Earth for human-rated spacecraft', 'Paved way for Artemis II crewed mission'],
    color: '#ffffff', flag: '🇺🇸', icon: '🌕',
    significance: 'Major',
  },
  {
    id: 8, name: 'Mars Perseverance Rover', country: 'USA', agency: 'NASA',
    launchYear: 2020, endYear: null, status: 'Active',
    type: 'Mars Rover', target: 'Mars',
    description: 'Searching for signs of ancient microbial life in Jezero Crater. Deployed the Ingenuity helicopter — the first powered aircraft on another planet.',
    achievements: ['First powered flight on another planet (Ingenuity)', 'Produced oxygen on Mars (MOXIE)', 'Collecting samples for future return to Earth'],
    color: '#ef5350', flag: '🇺🇸', icon: '🤖',
    significance: 'Historic',
  },
  {
    id: 9, name: 'Parker Solar Probe', country: 'USA', agency: 'NASA',
    launchYear: 2018, endYear: null, status: 'Active',
    type: 'Solar Probe', target: 'Sun',
    description: 'Closest spacecraft to the Sun ever launched. Flying through the solar corona to study solar wind, space weather and the Sun\'s outer atmosphere.',
    achievements: ['Closest approach to the Sun ever', 'First spacecraft to fly through solar corona', 'Fastest human-made object (700,000 km/h)'],
    color: '#ffd600', flag: '🇺🇸', icon: '☀️',
    significance: 'Historic',
  },
  {
    id: 10, name: 'New Horizons', country: 'USA', agency: 'NASA',
    launchYear: 2006, endYear: null, status: 'Active',
    type: 'Deep Space Probe', target: 'Pluto / Kuiper Belt',
    description: 'First spacecraft to visit Pluto, revealing a complex, geologically active world with mountains, plains and a heart-shaped nitrogen glacier.',
    achievements: ['First close-up images of Pluto', 'Discovered Pluto\'s moons in detail', 'Currently exploring Kuiper Belt'],
    color: '#81d4fa', flag: '🇺🇸', icon: '🔭',
    significance: 'Major',
  },
  {
    id: 11, name: 'SpaceX Crew Dragon', country: 'USA', agency: 'SpaceX/NASA',
    launchYear: 2020, endYear: null, status: 'Active',
    type: 'Crewed', target: 'ISS',
    description: 'First commercially built and operated crewed spacecraft to reach the ISS. Restored US human spaceflight capability after the Space Shuttle retirement.',
    achievements: ['First commercial crewed spacecraft to ISS', 'Reusable spacecraft for cost efficiency', 'Regular ISS crew rotation missions'],
    color: '#ab47bc', flag: '🇺🇸', icon: '🚀',
    significance: 'Major',
  },

  // ── INDIA / ISRO ─────────────────────────────────────────────────────────────
  {
    id: 12, name: 'Chandrayaan-1', country: 'India', agency: 'ISRO',
    launchYear: 2008, endYear: 2009, status: 'Completed',
    type: 'Lunar Orbiter', target: 'Moon',
    description: 'India\'s first lunar mission. Confirmed the presence of water ice on the Moon\'s surface — a groundbreaking discovery that changed our understanding of the Moon.',
    achievements: ['Confirmed water molecules on Moon', 'Mapped lunar surface in detail', 'India\'s first deep space mission'],
    color: '#ff9800', flag: '🇮🇳', icon: '🌕',
    significance: 'Historic',
  },
  {
    id: 13, name: 'Chandrayaan-2', country: 'India', agency: 'ISRO',
    launchYear: 2019, endYear: null, status: 'Partial Success',
    type: 'Lunar Orbiter/Lander', target: 'Moon',
    description: 'India\'s second lunar mission with orbiter, lander (Vikram), and rover (Pragyan). The lander crashed during descent, but the orbiter continues to operate successfully.',
    achievements: ['Orbiter still operational', 'High-resolution lunar surface mapping', 'Inspired Chandrayaan-3 development'],
    color: '#ff9800', flag: '🇮🇳', icon: '🌕',
    significance: 'Major',
  },
  {
    id: 14, name: 'Chandrayaan-3', country: 'India', agency: 'ISRO',
    launchYear: 2023, endYear: 2023, status: 'Completed',
    type: 'Lunar Lander/Rover', target: 'Moon South Pole',
    description: 'India\'s historic triumph! Successfully landed near the Moon\'s south pole on August 23, 2023 — the first country to do so. India became the 4th nation to achieve a soft Moon landing.',
    achievements: ['First soft landing near Moon\'s south pole', 'India became 4th country to land on Moon', 'Confirmed sulfur on lunar south pole', 'Pragyan rover operational'],
    color: '#ff9800', flag: '🇮🇳', icon: '🌕',
    significance: 'Historic',
  },
  {
    id: 15, name: 'Mangalyaan (MOM)', country: 'India', agency: 'ISRO',
    launchYear: 2013, endYear: 2022, status: 'Completed',
    type: 'Mars Orbiter', target: 'Mars',
    description: 'Mars Orbiter Mission — India\'s first interplanetary mission. Made India the first Asian nation to reach Mars orbit and the first in the world to succeed on its maiden attempt.',
    achievements: ['First Asian nation to reach Mars', 'First country to succeed on maiden Mars attempt', 'Completed on 1/10th the cost of similar missions'],
    color: '#ff9800', flag: '🇮🇳', icon: '🔴',
    significance: 'Historic',
  },
  {
    id: 16, name: 'Aditya-L1', country: 'India', agency: 'ISRO',
    launchYear: 2023, endYear: null, status: 'Active',
    type: 'Solar Observatory', target: 'Sun (L1 Point)',
    description: 'India\'s first dedicated solar mission, placed at the Sun-Earth L1 Lagrange point. Studies solar wind, coronal mass ejections and space weather.',
    achievements: ['India\'s first solar observatory', 'Reached L1 Lagrange point', 'Studying solar corona and space weather'],
    color: '#ffd600', flag: '🇮🇳', icon: '☀️',
    significance: 'Historic',
  },
  {
    id: 17, name: 'Gaganyaan', country: 'India', agency: 'ISRO',
    launchYear: 2025, endYear: null, status: 'Upcoming',
    type: 'Crewed', target: 'Low Earth Orbit',
    description: 'India\'s first crewed spaceflight mission. Will make India the 4th country in the world to independently launch humans into space.',
    achievements: ['Will be India\'s first crewed mission', 'Four astronauts training in Russia and India', 'TV-D1 abort test successful in 2023'],
    color: '#ff9800', flag: '🇮🇳', icon: '🚀',
    significance: 'Upcoming',
  },
  {
    id: 18, name: 'PSLV-C37', country: 'India', agency: 'ISRO',
    launchYear: 2017, endYear: 2017, status: 'Completed',
    type: 'Launch Vehicle', target: 'Low Earth Orbit',
    description: 'Set the world record by launching 104 satellites in a single mission, breaking the previous record of 37. Demonstrated India\'s cost-effective launch capabilities.',
    achievements: ['World record: 104 satellites in one launch', 'Satellites from 6 countries', 'Showcased ISRO\'s precision launch capability'],
    color: '#ff9800', flag: '🇮🇳', icon: '🚀',
    significance: 'Historic',
  },

  // ── RUSSIA / USSR ────────────────────────────────────────────────────────────
  {
    id: 19, name: 'Sputnik 1', country: 'Russia', agency: 'Soviet Space Program',
    launchYear: 1957, endYear: 1958, status: 'Completed',
    type: 'Satellite', target: 'Low Earth Orbit',
    description: 'The first artificial satellite to orbit Earth, launched on October 4, 1957. Its successful launch shocked the world and started the Space Age.',
    achievements: ['First artificial satellite', 'Started the Space Race', 'Proved orbital spaceflight was possible'],
    color: '#ef5350', flag: '🇷🇺', icon: '🛰️',
    significance: 'Historic',
  },
  {
    id: 20, name: 'Vostok 1 (Gagarin)', country: 'Russia', agency: 'Soviet Space Program',
    launchYear: 1961, endYear: 1961, status: 'Completed',
    type: 'Crewed', target: 'Low Earth Orbit',
    description: 'Yuri Gagarin became the first human in space on April 12, 1961. The 108-minute flight orbited Earth once and changed history forever.',
    achievements: ['First human in space', 'First orbital spaceflight', 'Yuri Gagarin became global hero'],
    crew: ['Yuri Gagarin'],
    color: '#ef5350', flag: '🇷🇺', icon: '🚀',
    significance: 'Historic',
  },
  {
    id: 21, name: 'Luna 9', country: 'Russia', agency: 'Soviet Space Program',
    launchYear: 1966, endYear: 1966, status: 'Completed',
    type: 'Lunar Lander', target: 'Moon',
    description: 'First spacecraft to achieve a soft landing on the Moon and transmit photographs from the lunar surface.',
    achievements: ['First soft Moon landing', 'First lunar surface photographs', 'Proved Moon surface was solid'],
    color: '#ef5350', flag: '🇷🇺', icon: '🌕',
    significance: 'Historic',
  },
  {
    id: 22, name: 'Mir Space Station', country: 'Russia', agency: 'Roscosmos',
    launchYear: 1986, endYear: 2001, status: 'Completed',
    type: 'Space Station', target: 'Low Earth Orbit',
    description: 'Operated for 15 years as the first permanently crewed space station. Hosted 125 cosmonauts and astronauts from 12 countries.',
    achievements: ['15 years in operation', '125 crew members from 12 countries', 'Pioneered long-duration spaceflight'],
    color: '#ef5350', flag: '🇷🇺', icon: '🛸',
    significance: 'Historic',
  },

  // ── EUROPE / ESA ─────────────────────────────────────────────────────────────
  {
    id: 23, name: 'Rosetta / Philae', country: 'Europe', agency: 'ESA',
    launchYear: 2004, endYear: 2016, status: 'Completed',
    type: 'Comet Mission', target: 'Comet 67P',
    description: 'First mission to orbit and land on a comet. Philae became the first spacecraft to successfully land on a comet nucleus in November 2014.',
    achievements: ['First comet orbit', 'First comet landing (Philae)', 'Detected organic molecules on comet'],
    color: '#4caf50', flag: '🇪🇺', icon: '☄️',
    significance: 'Historic',
  },
  {
    id: 24, name: 'Gaia', country: 'Europe', agency: 'ESA',
    launchYear: 2013, endYear: null, status: 'Active',
    type: 'Space Telescope', target: 'Milky Way',
    description: 'Creating the most detailed 3D map of our galaxy. Has catalogued over 1.8 billion stars with unprecedented precision.',
    achievements: ['Catalogue of 1.8 billion stars', 'Most detailed Milky Way map', '3D positions and motions of stars'],
    color: '#4caf50', flag: '🇪🇺', icon: '🔭',
    significance: 'Major',
  },
  {
    id: 25, name: 'Mars Express', country: 'Europe', agency: 'ESA',
    launchYear: 2003, endYear: null, status: 'Active',
    type: 'Mars Orbiter', target: 'Mars',
    description: 'ESA\'s first planetary mission. Has been orbiting Mars for over 20 years and discovered evidence of liquid water beneath the Martian south pole.',
    achievements: ['Evidence of underground liquid water on Mars', '20+ years of continuous Mars observation', 'Detailed Mars surface and atmosphere data'],
    color: '#4caf50', flag: '🇪🇺', icon: '🔴',
    significance: 'Major',
  },

  // ── CHINA / CNSA ─────────────────────────────────────────────────────────────
  {
    id: 26, name: 'Chang\'e 4', country: 'China', agency: 'CNSA',
    launchYear: 2018, endYear: null, status: 'Active',
    type: 'Lunar Lander/Rover', target: 'Moon Far Side',
    description: 'First spacecraft to land on the far side of the Moon. The Yutu-2 rover has operated for over 5 years, the longest-operating lunar rover in history.',
    achievements: ['First landing on Moon\'s far side', 'Longest operating lunar rover', 'Discovered new type of lunar rock'],
    color: '#f44336', flag: '🇨🇳', icon: '🌕',
    significance: 'Historic',
  },
  {
    id: 27, name: 'Tianwen-1', country: 'China', agency: 'CNSA',
    launchYear: 2020, endYear: null, status: 'Active',
    type: 'Mars Mission', target: 'Mars',
    description: 'China\'s first Mars mission combining an orbiter, lander, and rover (Zhurong). Made China the second country to successfully operate a rover on Mars.',
    achievements: ['China\'s first Mars mission', 'Second country to operate Mars rover', 'Complete Mars exploration in one mission'],
    color: '#f44336', flag: '🇨🇳', icon: '🔴',
    significance: 'Historic',
  },
  {
    id: 28, name: 'Tiangong Space Station', country: 'China', agency: 'CNSA',
    launchYear: 2021, endYear: null, status: 'Active',
    type: 'Space Station', target: 'Low Earth Orbit',
    description: 'China\'s permanent modular space station. Fully operational with three modules and regularly hosts Chinese astronauts (taikonauts).',
    achievements: ['China\'s first permanent space station', 'Full 3-module configuration completed', 'Regular crewed missions'],
    color: '#f44336', flag: '🇨🇳', icon: '🛸',
    significance: 'Major',
  },
  {
    id: 29, name: 'Chang\'e 5', country: 'China', agency: 'CNSA',
    launchYear: 2020, endYear: 2020, status: 'Completed',
    type: 'Lunar Sample Return', target: 'Moon',
    description: 'First lunar sample return mission in 44 years. Returned 1.7 kg of lunar rock and soil from the Mons Rümker volcanic formation.',
    achievements: ['First lunar sample return since 1976', 'Returned 1.7 kg of lunar samples', 'Youngest lunar samples ever collected'],
    color: '#f44336', flag: '🇨🇳', icon: '🌕',
    significance: 'Historic',
  },

  // ── JAPAN / JAXA ─────────────────────────────────────────────────────────────
  {
    id: 30, name: 'Hayabusa2', country: 'Japan', agency: 'JAXA',
    launchYear: 2014, endYear: 2020, status: 'Completed',
    type: 'Asteroid Mission', target: 'Asteroid Ryugu',
    description: 'Successfully collected samples from asteroid Ryugu and returned them to Earth. Brought back pristine material from a primitive carbon-rich asteroid.',
    achievements: ['First subsurface asteroid samples', 'Created artificial crater on asteroid', 'Returned pristine carbon-rich samples'],
    color: '#2196f3', flag: '🇯🇵', icon: '🌠',
    significance: 'Historic',
  },
  {
    id: 31, name: 'Akatsuki', country: 'Japan', agency: 'JAXA',
    launchYear: 2010, endYear: null, status: 'Active',
    type: 'Venus Orbiter', target: 'Venus',
    description: 'Japan\'s Venus Climate Orbiter studying the atmosphere and weather of Venus after a dramatic second attempt to enter orbit in 2015.',
    achievements: ['First Japanese planetary orbiter', 'Detailed Venus atmosphere study', 'Discovered bow-shaped wind pattern on Venus'],
    color: '#2196f3', flag: '🇯🇵', icon: '🌍',
    significance: 'Major',
  },

  // ── UAE ──────────────────────────────────────────────────────────────────────
  {
    id: 32, name: 'Emirates Mars Mission (Hope)', country: 'UAE', agency: 'MBRSC',
    launchYear: 2020, endYear: null, status: 'Active',
    type: 'Mars Orbiter', target: 'Mars',
    description: 'The Arab world\'s first interplanetary mission. Studies the Martian atmosphere and weather throughout a full Martian year, providing the first complete picture of Martian climate.',
    achievements: ['First Arab interplanetary mission', 'First complete picture of Mars atmosphere', 'Inspired generation of Arab scientists'],
    color: '#009900', flag: '🇦🇪', icon: '🔴',
    significance: 'Historic',
  },
];

// ─── Constants ────────────────────────────────────────────────────────────────
const COUNTRIES  = ['All', 'USA', 'India', 'Russia', 'Europe', 'China', 'Japan', 'UAE', 'International'];
const STATUSES   = ['All', 'Active', 'Completed', 'Partial Success', 'Upcoming'];
const TYPES      = ['All', 'Crewed', 'Crewed Lunar', 'Mars Rover', 'Mars Orbiter', 'Lunar Lander/Rover',
                    'Lunar Orbiter', 'Space Station', 'Space Telescope', 'Deep Space Probe',
                    'Solar Probe', 'Asteroid Mission', 'Venus Orbiter', 'Comet Mission',
                    'Solar Observatory', 'Launch Vehicle', 'Satellite'];
const SIGNIFICANCE = ['All', 'Historic', 'Major', 'Upcoming'];

const COUNTRY_FLAGS = {
  'USA': '🇺🇸', 'India': '🇮🇳', 'Russia': '🇷🇺',
  'Europe': '🇪🇺', 'China': '🇨🇳', 'Japan': '🇯🇵',
  'UAE': '🇦🇪', 'International': '🌍',
};

const STATUS_COLORS = {
  'Active':          '#66bb6a',
  'Completed':       '#ffffff',
  'Partial Success': '#ffa726',
  'Upcoming':        '#ab47bc',
};

const SIGNIFICANCE_COLORS = {
  'Historic': '#ffd600',
  'Major':    '#ffffff',
  'Upcoming': '#ab47bc',
};

// ─── Mission Card ─────────────────────────────────────────────────────────────
function MissionCard({ mission, onClick }) {
  const statusColor = STATUS_COLORS[mission.status] || '#ffffff';

  return (
    <Card onClick={onClick} sx={{
      cursor: 'pointer', height: '100%',
      border: `1px solid ${mission.color}33`,
      transition: 'all 0.2s', position: 'relative', overflow: 'hidden',
      '&:hover': { border: `1px solid ${mission.color}88`, transform: 'translateY(-3px)',
        boxShadow: `0 8px 24px ${mission.color}22` },
      '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0,
        height: 3, bgcolor: mission.color },
    }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 28 }}>{mission.icon}</Typography>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>
                {mission.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {mission.flag} {mission.agency}
              </Typography>
            </Box>
          </Box>
          {mission.significance === 'Historic' && (
            <Tooltip title="Historic Mission">
              <Typography sx={{ fontSize: 16 }}>⭐</Typography>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5, flexWrap: 'wrap' }}>
          <Chip label={mission.status} size="small"
            sx={{ bgcolor: `${statusColor}22`, color: statusColor,
              border: `1px solid ${statusColor}44`, fontWeight: 700, fontSize: 9 }} />
          <Chip label={mission.type} size="small"
            sx={{ bgcolor: `${mission.color}11`, color: mission.color,
              border: `1px solid ${mission.color}33`, fontWeight: 600, fontSize: 9 }} />
        </Box>

        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6,
          display: 'block', mb: 1.5,
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
          {mission.description}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
            🎯 {mission.target}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
            {mission.launchYear}{mission.endYear ? `–${mission.endYear}` : '–present'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Mission Detail Dialog ────────────────────────────────────────────────────
function MissionDetailDialog({ mission, onClose }) {
  if (!mission) return null;
  const statusColor = STATUS_COLORS[mission.status] || '#ffffff';

  return (
    <Dialog open={!!mission} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { bgcolor: 'background.paper', border: `1px solid ${mission.color}44` } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: 40 }}>{mission.icon}</Typography>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: mission.color }}>
              {mission.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip label={`${mission.flag} ${mission.country}`} size="small"
                sx={{ bgcolor: `${mission.color}22`, color: mission.color, fontWeight: 700, fontSize: 10 }} />
              <Chip label={mission.status} size="small"
                sx={{ bgcolor: `${statusColor}22`, color: statusColor, fontWeight: 700, fontSize: 10 }} />
              {mission.significance === 'Historic' && (
                <Chip label="⭐ Historic" size="small"
                  sx={{ bgcolor: 'rgba(255,214,0,0.15)', color: '#ffd600', fontWeight: 700, fontSize: 10 }} />
              )}
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      <DialogContent sx={{ pt: 2.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.9, mb: 3 }}>
          {mission.description}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Agency',         value: mission.agency },
            { label: 'Country',        value: `${mission.flag} ${mission.country}` },
            { label: 'Target',         value: `🎯 ${mission.target}` },
            { label: 'Mission Type',   value: mission.type },
            { label: 'Launch Year',    value: mission.launchYear },
            { label: 'End Year',       value: mission.endYear || 'Ongoing' },
          ].map(({ label, value }) => (
            <Grid item xs={6} sm={4} key={label}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 9,
                  textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Key Achievements */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.light', mb: 1.5,
          textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
          🏆 Key Achievements
        </Typography>
        <Box sx={{ mb: 3 }}>
          {mission.achievements.map((ach, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'flex-start' }}>
              <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: `${mission.color}33`,
                border: `1px solid ${mission.color}`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                <Typography sx={{ fontSize: 10, color: mission.color, fontWeight: 800 }}>{i + 1}</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                {ach}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Crew if available */}
        {mission.crew && mission.crew.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.light', mb: 1.5,
              textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
              👨‍🚀 Crew Members
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {mission.crew.map(member => (
                <Chip key={member} label={member}
                  sx={{ bgcolor: `${mission.color}22`, color: mission.color,
                    border: `1px solid ${mission.color}44`, fontWeight: 600 }} />
              ))}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Timeline View ────────────────────────────────────────────────────────────
function TimelineView({ missions, onSelect }) {
  const byDecade = useMemo(() => {
    const decades = {};
    missions.forEach(m => {
      const decade = Math.floor(m.launchYear / 10) * 10;
      if (!decades[decade]) decades[decade] = [];
      decades[decade].push(m);
    });
    return Object.entries(decades).sort(([a], [b]) => Number(a) - Number(b));
  }, [missions]);

  return (
    <Box sx={{ position: 'relative', pl: 4 }}>
      {/* Vertical line */}
      <Box sx={{ position: 'absolute', left: 16, top: 0, bottom: 0, width: 2,
        bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1 }} />

      {byDecade.map(([decade, ms]) => (
        <Box key={decade} sx={{ mb: 4 }}>
          {/* Decade marker */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'primary.dark',
              border: '2px solid', borderColor: 'primary.main', display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              position: 'absolute', left: 0 }}>
              <Typography sx={{ fontSize: 9, fontWeight: 800, color: 'primary.light' }}>
                {decade}s
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.light', ml: 2 }}>
              {decade}s
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {ms.map(mission => {
              const statusColor = STATUS_COLORS[mission.status] || '#ffffff';
              return (
                <Card key={mission.id} onClick={() => onSelect(mission)}
                  sx={{ cursor: 'pointer', border: `1px solid ${mission.color}33`,
                    '&:hover': { border: `1px solid ${mission.color}88`, bgcolor: `${mission.color}08` },
                    transition: 'all 0.2s' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontSize: 24, flexShrink: 0 }}>{mission.icon}</Typography>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: mission.color }}>
                            {mission.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {mission.flag} {mission.launchYear}
                          </Typography>
                          {mission.significance === 'Historic' && <Typography sx={{ fontSize: 12 }}>⭐</Typography>}
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {mission.description.slice(0, 100)}...
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                        <Chip label={mission.status} size="small"
                          sx={{ bgcolor: `${statusColor}22`, color: statusColor, fontSize: 9, fontWeight: 700, height: 18 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SpaceMissionsPage() {
  const [search, setSearch]           = useState('');
  const [countryFilter, setCountry]   = useState('All');
  const [statusFilter, setStatus]     = useState('All');
  const [typeFilter, setType]         = useState('All');
  const [sigFilter, setSig]           = useState('All');
  const [sortBy, setSortBy]           = useState('year');
  const [viewMode, setViewMode]       = useState('grid');
  const [selected, setSelected]       = useState(null);
  const [activeTab, setTab]           = useState(0);

  const filtered = useMemo(() => {
    let result = [...MISSIONS];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(m =>
        m.name.toLowerCase().includes(q) ||
        m.agency.toLowerCase().includes(q) ||
        m.target.toLowerCase().includes(q) ||
        m.country.toLowerCase().includes(q)
      );
    }
    if (countryFilter !== 'All') result = result.filter(m => m.country === countryFilter);
    if (statusFilter  !== 'All') result = result.filter(m => m.status === statusFilter);
    if (typeFilter    !== 'All') result = result.filter(m => m.type === typeFilter);
    if (sigFilter     !== 'All') result = result.filter(m => m.significance === sigFilter);

    result.sort((a, b) => {
      if (sortBy === 'year') return b.launchYear - a.launchYear;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return 0;
    });
    return result;
  }, [search, countryFilter, statusFilter, typeFilter, sigFilter, sortBy]);

  const stats = useMemo(() => ({
    total:    MISSIONS.length,
    active:   MISSIONS.filter(m => m.status === 'Active').length,
    historic: MISSIONS.filter(m => m.significance === 'Historic').length,
    india:    MISSIONS.filter(m => m.country === 'India').length,
    upcoming: MISSIONS.filter(m => m.status === 'Upcoming').length,
    countries: [...new Set(MISSIONS.map(m => m.country))].length,
  }), []);

  const clearFilters = () => {
    setSearch(''); setCountry('All'); setStatus('All');
    setType('All'); setSig('All');
  };

  const activeFiltersCount = [
    countryFilter !== 'All', statusFilter !== 'All',
    typeFilter !== 'All', sigFilter !== 'All', !!search,
  ].filter(Boolean).length;

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            🚀 Space Missions Explorer
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {stats.total} missions from {stats.countries} countries • Past, present & future
          </Typography>
        </Box>
        <ToggleButtonGroup value={viewMode} exclusive size="small"
          onChange={(_, v) => v && setViewMode(v)}>
          <ToggleButton value="grid"     sx={{ color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } }}><GridView fontSize="small" /></ToggleButton>
          <ToggleButton value="list"     sx={{ color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } }}><ViewList fontSize="small" /></ToggleButton>
          <ToggleButton value="timeline" sx={{ color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } }}><Timeline fontSize="small" /></ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ── Stats ──────────────────────────────────────────────────────────── */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {[
          { label: 'Total Missions',    value: stats.total,    color: '#ffffff', icon: '🚀' },
          { label: 'Active Now',        value: stats.active,   color: '#66bb6a', icon: '✅' },
          { label: 'Historic',          value: stats.historic, color: '#ffd600', icon: '⭐' },
          { label: 'Indian Missions',   value: stats.india,    color: '#ff9800', icon: '🇮🇳' },
          { label: 'Upcoming',          value: stats.upcoming, color: '#ab47bc', icon: '🔮' },
          { label: 'Countries',         value: stats.countries,color: '#ef5350', icon: '🌍' },
        ].map(({ label, value, color, icon }) => (
          <Grid item xs={6} sm={4} md={2} key={label}>
            <Card sx={{ border: `1px solid ${color}33`, textAlign: 'center' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography sx={{ fontSize: 20, mb: 0.3 }}>{icon}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color }}>{value}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 9 }}>{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Country Filter Tabs ─────────────────────────────────────────────── */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {COUNTRIES.map(c => (
          <Chip
            key={c}
            label={`${COUNTRY_FLAGS[c] || '🌍'} ${c}`}
            onClick={() => setCountry(c)}
            sx={{
              cursor: 'pointer', fontWeight: 700,
              bgcolor: countryFilter === c ? 'rgba(255,255,255,0.2)' : 'transparent',
              color:   countryFilter === c ? 'primary.light' : 'text.secondary',
              border:  countryFilter === c ? '1px solid rgba(255,255,255,0.6)' : '1px solid rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          />
        ))}
      </Box>

      {/* ── Search & Filters ───────────────────────────────────────────────── */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search missions, agencies, targets..."
              value={search} onChange={e => setSearch(e.target.value)}
              size="small" sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary', fontSize: 18 }} /></InputAdornment>,
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}><Clear sx={{ fontSize: 14 }} /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={e => setStatus(e.target.value)}>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Significance</InputLabel>
              <Select value={sigFilter} label="Significance" onChange={e => setSig(e.target.value)}>
                {SIGNIFICANCE.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} label="Sort By" onChange={e => setSortBy(e.target.value)}>
                <MenuItem value="year">Year (Newest)</MenuItem>
                <MenuItem value="name">Name (A-Z)</MenuItem>
              </Select>
            </FormControl>
            {activeFiltersCount > 0 && (
              <Button size="small" onClick={clearFilters} startIcon={<Clear />}
                sx={{ color: 'text.secondary' }}>
                Clear ({activeFiltersCount})
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Results count */}
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Showing <strong style={{ color: '#ffffff' }}>{filtered.length}</strong> of {MISSIONS.length} missions
        {countryFilter !== 'All' && ` from ${countryFilter}`}
      </Typography>

      {/* ── Grid View ──────────────────────────────────────────────────────── */}
      {viewMode === 'grid' && (
        <Grid container spacing={2}>
          {filtered.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Rocket sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>No missions found</Typography>
                <Button onClick={clearFilters} sx={{ mt: 2, color: 'primary.main' }}>Clear filters</Button>
              </Box>
            </Grid>
          ) : filtered.map(m => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={m.id}>
              <MissionCard mission={m} onClick={() => setSelected(m)} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── List View ──────────────────────────────────────────────────────── */}
      {viewMode === 'list' && (
        <Card sx={{ border: '1px solid rgba(255,255,255,0.1)' }}>
          <Box>
            {filtered.map((m, idx) => {
              const statusColor = STATUS_COLORS[m.status] || '#ffffff';
              return (
                <Box key={m.id} onClick={() => setSelected(m)} sx={{
                  display: 'flex', gap: 2, p: 2, cursor: 'pointer', alignItems: 'flex-start',
                  borderBottom: idx < filtered.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' }, transition: 'all 0.15s',
                }}>
                  <Typography sx={{ fontSize: 28, flexShrink: 0 }}>{m.icon}</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: m.color }}>{m.name}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{m.flag} {m.agency} • {m.launchYear}</Typography>
                      {m.significance === 'Historic' && <Typography sx={{ fontSize: 12 }}>⭐</Typography>}
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      {m.description.slice(0, 120)}...
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                    <Chip label={m.status} size="small"
                      sx={{ bgcolor: `${statusColor}22`, color: statusColor, fontSize: 9, fontWeight: 700 }} />
                    <Chip label={m.target} size="small"
                      sx={{ bgcolor: `${m.color}11`, color: m.color, fontSize: 9 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Card>
      )}

      {/* ── Timeline View ──────────────────────────────────────────────────── */}
      {viewMode === 'timeline' && (
        <TimelineView missions={filtered} onSelect={setSelected} />
      )}

      {/* Detail Dialog */}
      <MissionDetailDialog mission={selected} onClose={() => setSelected(null)} />
    </Box>
  );
}